import React from 'react';
import {
  ChevronRightIcon,
  FolderIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import { ResourceFolder, ResourceLink } from '../../types';
import { t } from '../../utils/translations';
import RowActions from '../ui/RowActions';
import ResourceKindIcon, { resourceKindShortLabel } from './ResourceKindIcon';
import {
  ResourceFolderSearchResult,
  ResourceSearchResult,
  findResourceHighlightRanges,
  folderIconClass,
  openResourceUrl,
} from './resourcesUtils';

interface ResourceSearchResultsProps {
  folderResults: ResourceFolderSearchResult[];
  results: ResourceSearchResult[];
  query: string;
  canManage: boolean;
  onOpenFolder: (folder: ResourceFolder) => void;
  onEditLink: (link: ResourceLink) => void;
  onDeleteLink: (link: ResourceLink) => void;
}

const Highlighted: React.FC<{ text: string; query: string }> = ({ text, query }) => {
  const ranges = findResourceHighlightRanges(text, query);
  if (ranges.length === 0) return <>{text}</>;

  const parts: React.ReactNode[] = [];
  let cursor = 0;
  ranges.forEach(([start, end], index) => {
    if (start > cursor) parts.push(text.slice(cursor, start));
    parts.push(
      <mark key={`${start}-${end}-${index}`} className="rounded bg-brand-100 px-0.5 text-brand-800">
        {text.slice(start, end)}
      </mark>
    );
    cursor = end;
  });
  if (cursor < text.length) parts.push(text.slice(cursor));

  return <>{parts}</>;
};

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">{children}</p>
);

function countLabel(count: number, singular: string, plural: string): string {
  return `${count} ${count === 1 ? singular : plural}`;
}

function formatFolderContents(childFolderCount: number, linkCount: number): string {
  if (childFolderCount === 0 && linkCount === 0) return t.resources.emptyFolderShort;
  const parts: string[] = [];
  if (childFolderCount > 0) {
    parts.push(
      countLabel(childFolderCount, t.resources.folderCountSingular, t.resources.folderCountPlural)
    );
  }
  if (linkCount > 0) {
    parts.push(countLabel(linkCount, t.resources.linkCountSingular, t.resources.linkCountPlural));
  }
  return parts.join(' · ');
}

const ResourceSearchResults: React.FC<ResourceSearchResultsProps> = ({
  folderResults,
  results,
  query,
  canManage,
  onOpenFolder,
  onEditLink,
  onDeleteLink,
}) => {
  if (folderResults.length === 0 && results.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-slate-200 bg-white px-6 py-14 text-center">
        <MagnifyingGlassIcon className="mx-auto h-10 w-10 text-slate-300" />
        <p className="mt-3 text-sm font-medium text-slate-700">{t.resources.searchNoResults}</p>
        <p className="mt-1 text-xs text-slate-500">{t.resources.searchNoResultsHint}</p>
      </div>
    );
  }

  const showSectionLabels = folderResults.length > 0;

  return (
    <div className="space-y-5">
      {folderResults.length > 0 && (
        <div>
          <SectionLabel>{t.resources.searchSectionFolders}</SectionLabel>
          <div className="divide-y divide-slate-100 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            {folderResults.map(({ folder, parentPathLabel, linkCount, childFolderCount }) => (
              <button
                key={folder.id}
                type="button"
                onClick={() => onOpenFolder(folder)}
                title={t.resources.openFolderInTree}
                className="group flex w-full items-center gap-3 px-3 py-3 text-left hover:bg-slate-50 sm:px-4"
              >
                <FolderIcon className={`h-5 w-5 shrink-0 ${folderIconClass(folder.color)}`} />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-slate-800 group-hover:text-brand-700">
                    <Highlighted text={folder.name} query={query} />
                  </span>
                  {parentPathLabel && (
                    <span className="mt-0.5 block truncate text-xs text-slate-500">
                      {parentPathLabel}
                    </span>
                  )}
                </span>
                <span className="shrink-0 text-xs text-slate-500">
                  {formatFolderContents(childFolderCount, linkCount)}
                </span>
                <ChevronRightIcon className="h-4 w-4 shrink-0 text-slate-300 group-hover:text-slate-500" />
              </button>
            ))}
          </div>
        </div>
      )}

      {results.length > 0 && (
        <div>
          {showSectionLabels && <SectionLabel>{t.resources.searchSectionLinks}</SectionLabel>}
          <div className="divide-y divide-slate-100 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            {results.map(({ link, folder, pathLabel }) => (
              <div
                key={link.id}
                className="group flex items-center gap-3 px-3 py-3 hover:bg-slate-50 sm:px-4"
              >
                <ResourceKindIcon kind={link.kind} className="mt-0.5 h-5 w-5 shrink-0" />
                <button
                  type="button"
                  onClick={() => openResourceUrl(link.url)}
                  title={t.resources.openInGoogle}
                  className="min-w-0 flex-1 text-left"
                >
                  <span className="block truncate text-sm font-medium text-slate-800 group-hover:text-brand-700">
                    <Highlighted text={link.title} query={query} />
                  </span>
                  <span className="mt-0.5 flex items-center gap-1.5 text-xs text-slate-500">
                    <FolderIcon className={`h-3.5 w-3.5 shrink-0 ${folderIconClass(folder.color)}`} />
                    <span className="truncate">
                      <Highlighted text={pathLabel} query={query} />
                    </span>
                  </span>
                </button>
                <span className="hidden shrink-0 rounded-md bg-slate-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-slate-600 sm:inline">
                  {resourceKindShortLabel(link.kind)}
                </span>
                {canManage && (
                  <RowActions
                    onEdit={() => onEditLink(link)}
                    onDelete={() => onDeleteLink(link)}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ResourceSearchResults;
